import React, {useCallback, useMemo} from 'react';

import {isSystemMetric} from '../../../../util/plotHelpers/prettifyMetricName';
import {globalXAxisOptions} from '../../../../util/runhelpers';
import {useKeyInfoQueryContext} from '../../../MultiRunWorkspace/KeyInfoQueryContext';
import {SettingLabel} from '../components/SettingLabel';
import {SettingSelect} from '../components/SettingSelect';
import {
  SemanticLegendSettings,
  SettingSelectOptionType,
  YAxisValues,
} from '../types';
import {xAxisLabel} from '../utils';
import {DEFAULT_X_AXIS_OPTION_VALUES} from './SemanticLegendDefaults';

export type SemanticLegendKeyProps = {
  currentKey: string;
  type: YAxisValues;
  updateSetting: (settings: Partial<SemanticLegendSettings>) => void;
  configKeys: string[];
};

export const SemanticLegendKey = ({
  updateSetting,
  currentKey,
  type,
  configKeys,
}: SemanticLegendKeyProps) => {
  const metricOptions = useWorkspaceMetricOptions();
  const configKeyOptions = useWorkspaceConfigKeyOptions(configKeys);
  const keyOptions = useMemo(() => {
    return [...metricOptions, ...configKeyOptions];
  }, [metricOptions, configKeyOptions]);
  const keyValue = useMemo(() => {
    const isMetric = (metricOptions ?? []).some(
      opt => opt.value === currentKey
    );
    // If the current key is a metric, return the metric label
    if (isMetric && currentKey != null) {
      return {
        value: currentKey,
        label: xAxisLabel(currentKey),
      };
    } else if (configKeyOptions.some(opt => opt.value === currentKey)) {
      // If the current key is a config key, return the config key label
      return {
        value: currentKey,
        label: configKeyLabel(currentKey),
      };
    }
    return {
      value: '',
      label: '',
    };
  }, [currentKey, metricOptions, configKeyOptions]);

  const onKeyChange = useCallback(
    (
      selected: SettingSelectOptionType | null,
      configKeyOptions: {label: string; value: string}[],
      type: YAxisValues
    ) => {
      if (selected != null) {
        let settingsToUpdate: Partial<SemanticLegendSettings> = {
          key: selected.value,
        };
        // If the selected key is a config key, update the yAxisValue to Config_Number
        if (configKeyOptions.some(opt => opt.value === selected.value)) {
          settingsToUpdate = {
            ...settingsToUpdate,
            yAxisValue: YAxisValues.Config_Number,
          };
        } else {
          // If the selected key is a metric and the previous selected key was a config key, default the yAxisValue to Last
          if (type === YAxisValues.Config_Number) {
            settingsToUpdate = {
              ...settingsToUpdate,
              yAxisValue: YAxisValues.Last,
            };
          }
        }
        updateSetting(settingsToUpdate);
      }
    },
    [updateSetting]
  );

  return (
    <div className="flex flex-col" data-test="metric-setting">
      <div className="flex w-full">
        <SettingLabel text="Key" className="ml-14 w-[87px]" />
        <div className="item-center flex w-full flex-row gap-8">
          <SettingSelect
            className="grow"
            options={keyOptions}
            value={keyValue}
            onChange={selected => onKeyChange(selected, configKeyOptions, type)}
          />
        </div>
      </div>
    </div>
  );
};

export const useWorkspaceMetricOptions = () => {
  const {keyInfoQuery} = useKeyInfoQueryContext();
  const historyKeyXAxis = useMemo(
    () => globalXAxisOptions(keyInfoQuery.historyKeyInfo),
    [keyInfoQuery.historyKeyInfo]
  );

  const metricValues = useMemo(() => {
    return DEFAULT_X_AXIS_OPTION_VALUES.concat(historyKeyXAxis)
      .filter(val => !isSystemMetric(val))
      .map(val => ({
        label: xAxisLabel(val),
        value: val,
      }));
  }, [historyKeyXAxis]);

  return metricValues;
};

export const useWorkspaceConfigKeyOptions = (configKeys: string[]) => {
  const configValues = useMemo(() => {
    return configKeys.map(key => ({
      label: configKeyLabel(key),
      value: key,
    }));
  }, [configKeys]);
  return configValues;
};

const configKeyLabel = (key: string) => {
  return key.replace('config.', '').replace('.value', '');
};
